import { useEffect, useRef, useState } from 'react';
import { documentTypeOptions, reportCategoryOptions, type DocumentType, type ReportCategory } from '@command-center/admin-ecorp';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input } from '@command-center/ui-system';
import { CheckCircle2, FileUp, FolderKanban, Loader2, Search, Sparkles } from 'lucide-react';
import { extractDocument, listRecords, uploadDocument, type PortalRecord } from '../api';
import { display, formatBytes, useToast } from '../lib';

type Phase = 'idle' | 'uploading' | 'queuing' | 'queued';

/** Search-and-select a Project; used only when UploadDialog has no fixed projectId (Documents page). */
function ProjectPicker({ value, onChange }: { value?: PortalRecord; onChange(next?: PortalRecord): void }) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<PortalRecord[]>([]);
  const [openList, setOpenList] = useState(false);
  useEffect(() => {
    const text = query.trim();
    if (!text) { setOptions([]); return; }
    const timer = window.setTimeout(() => {
      void listRecords('projects', new URLSearchParams({ pageSize: '6', search: text })).then(page => setOptions(page.items)).catch(() => setOptions([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
        <span className="inline-flex items-center gap-2"><FolderKanban className="h-4 w-4 text-primary"/>{display(value.fields['Project Name'])}</span>
        <Button size="sm" variant="ghost" onClick={() => onChange(undefined)}>Change</Button>
      </div>
    );
  }
  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground"/>
        <Input className="pl-9" placeholder="Search projects…" value={query} onChange={e => { setQuery(e.target.value); setOpenList(true); }} onFocus={() => setOpenList(true)}/>
      </div>
      {openList && query.trim() && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border bg-popover p-1 shadow-lg">
          {options.map(option => (
            <button key={option.id} type="button" className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => { onChange(option); setOpenList(false); }}>
              <span>{display(option.fields['Project Name'])}</span>
            </button>
          ))}
          {!options.length && <p className="px-3 py-2 text-sm text-muted-foreground">No matching projects.</p>}
        </div>
      )}
    </div>
  );
}

/**
 * Upload a document into a Project. Every upload creates one Project
 * Document; site plans (and any type, on request) are queued for background
 * OpenAI extraction — this dialog never waits on that job, so it can never
 * be caught mid-processing. Omit `projectId` to show a project-picker step
 * first (used from the Documents page, which has no fixed project context).
 */
export function UploadDialog({ open, onClose, projectId: fixedProjectId, onUploaded }: {
  open: boolean;
  onClose(): void;
  projectId?: string;
  onUploaded(item: PortalRecord, options: { extractionQueued: boolean }): void;
}) {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [project, setProject] = useState<PortalRecord>();
  const [file, setFile] = useState<File>();
  const [documentType, setDocumentType] = useState<DocumentType>('Site Plan');
  const [reportCategory, setReportCategory] = useState<ReportCategory | ''>('');
  const [documentName, setDocumentName] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [revision, setRevision] = useState('');
  const [processNow, setProcessNow] = useState(true);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string>();

  const projectId = fixedProjectId ?? project?.id;

  function reset() { setProject(undefined); setFile(undefined); setDocumentType('Site Plan'); setReportCategory(''); setDocumentName(''); setDocumentDate(''); setRevision(''); setProcessNow(true); setPhase('idle'); setError(undefined); }
  function closeAndReset() { reset(); onClose(); }

  async function submit() {
    if (!file || !projectId || phase !== 'idle') return;
    setError(undefined);
    setPhase('uploading');
    try {
      const result = await uploadDocument(file, { projectId, documentType, reportCategory: documentType === 'Reports' && reportCategory ? reportCategory : undefined, documentName: documentName || undefined, documentDate: documentDate || undefined, revision: revision || undefined });
      if (!result.item) throw new Error('The upload succeeded but the document record was not returned.');
      toast(`Uploaded ${file.name}.`);
      let extractionQueued = false;
      if (processNow) {
        setPhase('queuing');
        try { await extractDocument(result.item.id); extractionQueued = true; }
        catch (cause) { toast(cause instanceof Error ? cause.message : 'Could not queue extraction. You can retry from the document.', 'error'); }
      }
      onUploaded(result.item, { extractionQueued });
      setPhase('queued');
    } catch (cause) {
      setPhase('idle');
      setError(cause instanceof Error ? cause.message : 'Upload failed.');
    }
  }

  const busy = phase === 'uploading' || phase === 'queuing';
  return (
    <Dialog open={open} onOpenChange={value => { if (!value && !busy) closeAndReset(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
        {phase === 'queued' ? (
          <>
            <div className="flex items-start gap-3 rounded-xl border bg-card p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-500"/>
              <div className="text-sm">
                <p className="font-medium text-foreground">Uploaded{processNow ? ' — extraction is processing in the background' : ''}.</p>
                {processNow && <p className="mt-1 text-muted-foreground">You can close this and keep working. A badge appears on Documents once it&apos;s ready to review.</p>}
              </div>
            </div>
            <DialogFooter><Button onClick={closeAndReset}>Close</Button></DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4">
              {!fixedProjectId && (
                <div className="field-label"><span>Project</span><ProjectPicker value={project} onChange={setProject}/></div>
              )}
              <button
                type="button"
                className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed p-8 text-muted-foreground transition hover:border-primary/50 hover:text-foreground"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
              >
                <FileUp className="h-8 w-8"/>
                {file ? <span className="font-medium text-foreground">{file.name} <span className="font-normal text-muted-foreground">({formatBytes(file.size)})</span></span> : <span>Choose a PDF, PNG, JPG, or WEBP</span>}
              </button>
              <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={e => { const next = e.target.files?.[0]; if (next) { setFile(next); if (!documentName) setDocumentName(next.name.replace(/\.[^.]+$/, '')); } }}/>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="field-label sm:col-span-2">Document name<input className="field-control" value={documentName} onChange={e => setDocumentName(e.target.value)} placeholder="e.g. Site Plan — Fuel Depot"/></label>
                <label className="field-label">Document type<select className="field-control" value={documentType} onChange={e => setDocumentType(e.target.value as DocumentType)}>{documentTypeOptions.map(option => <option key={option} value={option}>{option}</option>)}</select></label>
                {documentType === 'Reports' && <label className="field-label">Report category<select className="field-control" value={reportCategory} onChange={e => setReportCategory(e.target.value as ReportCategory | '')}><option value="">—</option>{reportCategoryOptions.map(option => <option key={option} value={option}>{option}</option>)}</select></label>}
                <label className="field-label">Document date<input className="field-control" type="date" value={documentDate} onChange={e => setDocumentDate(e.target.value)}/></label>
                <label className="field-label">Revision / version<input className="field-control" value={revision} onChange={e => setRevision(e.target.value)} placeholder="e.g. 2"/></label>
                <label className="field-label flex-row items-center gap-2 self-end"><input type="checkbox" checked={processNow} onChange={e => setProcessNow(e.target.checked)}/><span className="inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-primary"/>Process with AI after upload</span></label>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              {busy && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin"/>{phase === 'uploading' ? 'Uploading to private storage…' : 'Queuing extraction…'}</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" disabled={busy} onClick={closeAndReset}>Cancel</Button>
              <Button disabled={!file || !projectId || busy} onClick={() => void submit()}>{busy ? 'Working…' : 'Upload'}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
